/**
 * presenceChannel.ts — Singleton del canale Supabase Realtime Presence.
 *
 * PROBLEMA: Supabase de-duplica i canali per nome. Se due hook chiamano
 * supabase.channel('site_visitors') separatamente e poi .subscribe() ognuno
 * con la propria config, il secondo sovrascrive il primo e il canale diventa
 * instabile → widget mostra sempre "0 visitatori".
 *
 * SOLUZIONE: Un'unica istanza del canale condivisa tra tutti i consumer.
 * - usePresenceTracker chiama .track() su questa istanza
 * - useOnlineVisitors aggiunge listener .on('presence', ...) su questa istanza
 * - Il canale viene sottoscritto UNA SOLA VOLTA
 */

import { supabase } from '@/integrations/supabase/client';
import { isBot } from '@/utils/botDetection';

type PresenceChannel = ReturnType<typeof supabase.channel>;

let channelInstance: PresenceChannel | null = null;
let subscribePromise: Promise<void> | null = null;

/**
 * Restituisce sempre la stessa istanza del canale 'site_visitors'.
 * Se l'user-agent è un bot/crawler, restituisce null per evitare
 * connessioni WebSocket che fallirebbero (ERR_NAME_NOT_RESOLVED in GSC).
 *
 * @returns { channel, ready } — il canale (o null per bot) e una Promise.
 */
export function getPresenceChannel(): { channel: PresenceChannel | null; ready: Promise<void> } {
  if (isBot()) {
    return { channel: null, ready: Promise.resolve() };
  }

  if (!channelInstance) {
    channelInstance = supabase.channel('site_visitors');

    // Register presence listeners BEFORE subscribe — required by Supabase JS v2
    // to activate internal presence state tracking. Without this,
    // presenceState() always returns {} regardless of .track() calls.
    channelInstance.on('presence', { event: 'sync' }, () => {});
    channelInstance.on('presence', { event: 'join' }, () => {});
    channelInstance.on('presence', { event: 'leave' }, () => {});

    subscribePromise = new Promise<void>((resolve) => {
      channelInstance!.subscribe((status) => {
        if (status === 'SUBSCRIBED') {
          resolve();
        }
      });
    });
  }

  return { channel: channelInstance, ready: subscribePromise! };
}

/**
 * Distrugge il singleton. Da chiamare SOLO all'unmount dell'app root
 * (es. in usePresenceTracker quando il componente AppInner smonta).
 */
export function destroyPresenceChannel(): void {
  if (channelInstance) {
    supabase.removeChannel(channelInstance);
    channelInstance = null;
    subscribePromise = null;
  }
}
